import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import clsx from 'clsx'
import {useAuth} from '../../../../app/modules/auth'
import {useGlobalSearch, useSearchCategories, MIN_SEARCH_LENGTH} from '../../../../app/modules/apps/search/core/useGlobalSearch'
import {getAccessiblePages, searchPages, pagesAsQuickLinks} from '../../../../app/modules/apps/search/core/pageIndex'
import {DisplayItem} from '../../../../app/modules/apps/search/core/_models'

const RECENT_KEY = 'global_search_recent'
const MAX_RECENT = 5

const CATEGORY_ICONS: Record<string, string> = {
  Dashboard: '📊',
  Clients: '🏢',
  Invoices: '📄',
  Products: '📦',
  'Billing Rates': '💵',
  Users: '👤',
  CDR: '📞',
  Roles: '🛡️',
  'Data Sources': '🗄️',
  'Global Settings': '⚙️',
  'Activity Log': '📜',
  Pages: '🧭',
  Actions: '⚡',
}

/** Kategori tab → menu key yang dibutuhkan (selaras dengan provider backend). */
const SERVER_CATEGORIES: {category: string; menuKey: string; clientAllowed: boolean}[] = [
  {category: 'Clients', menuKey: 'clients', clientAllowed: false},
  {category: 'Invoices', menuKey: 'invoice', clientAllowed: true},
  {category: 'Products', menuKey: 'products', clientAllowed: false},
  {category: 'Billing Rates', menuKey: 'billing_rates', clientAllowed: false},
  {category: 'Users', menuKey: 'user_management', clientAllowed: true},
  {category: 'Data Sources', menuKey: 'data_source', clientAllowed: false},
]

/** Tab halaman khusus sistem yang dapat difilter */
const EXTRA_TABS: {category: string; canShow: (has: (k: string) => boolean, isSuper: boolean, isClient: boolean) => boolean}[] = [
  {category: 'Dashboard', canShow: (has, isSuper) => has('dashboard') || isSuper},
  {category: 'CDR', canShow: (has, isSuper) => has('cdr_report') || isSuper},
  {category: 'Roles', canShow: (has) => has('role_management')},
  {category: 'Global Settings', canShow: (has) => has('global_settings')},
  {category: 'Activity Log', canShow: (has, isSuper, isClient) => has('activity_log') || isSuper || isClient},
]

const loadRecent = (): DisplayItem[] => {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : []
  } catch {
    return []
  }
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Tandai bagian teks yang cocok dengan kata kunci. */
const Highlight: React.FC<{text?: string | null; term: string}> = ({text, term}) => {
  if (!text) return null
  const q = term.trim()
  if (q.length < MIN_SEARCH_LENGTH) return <>{text}</>
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'))
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase() ? (
          <mark key={i} className='ccs-search-mark'>
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  )
}

interface DisplayGroup {
  category: string
  total?: number
  items: DisplayItem[]
}

export const HeaderGeneralSearch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [activeIndex, setActiveIndex] = useState(0)
  const [recent, setRecent] = useState<DisplayItem[]>(loadRecent)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  // Hak akses (selaras dengan PrivateRoutes.tsx). Server tetap melakukan validasi ulang.
  const roleName = (currentUser?.role?.name || '').toUpperCase()
  const allowedMenus = useMemo(
    () => (currentUser?.role?.allowedMenus || '').split(',').map((m: string) => m.trim()).filter(Boolean),
    [currentUser?.role?.allowedMenus]
  )
  const isClientUser = !!(currentUser as any)?.clientId || roleName === 'CLIENT'
  const isSuperRole = !isClientUser && (roleName === 'ADMIN' || roleName === 'SUPERADMIN')

  const accessiblePages = useMemo(() => getAccessiblePages(allowedMenus, roleName), [allowedMenus, roleName])

  // Ambil daftar kategori yang diizinkan untuk role user ini langsung dari backend/database
  const {data: serverAllowedCategories} = useSearchCategories(isOpen)

  const availableServerCategories = useMemo(
    () =>
      SERVER_CATEGORIES.filter(
        (c) => (isClientUser ? c.clientAllowed : true) && (isSuperRole || allowedMenus.includes(c.menuKey))
      ).map((c) => c.category),
    [allowedMenus, isClientUser, isSuperRole]
  )

  const availableExtraCategories = useMemo(
    () =>
      EXTRA_TABS.filter((t) =>
        t.canShow((k) => allowedMenus.includes(k), isSuperRole, isClientUser)
      ).map((t) => t.category),
    [allowedMenus, isSuperRole, isClientUser]
  )

  const categories = useMemo(() => {
    // Prioritaskan daftar kategori dinamis yang dikembalikan oleh backend jika tersedia
    if (serverAllowedCategories && serverAllowedCategories.length > 0) {
      return serverAllowedCategories
    }
    return ['All', ...availableServerCategories, ...availableExtraCategories, 'Pages']
  }, [serverAllowedCategories, availableServerCategories, availableExtraCategories])
  const canSearchCdr = isSuperRole || allowedMenus.includes('cdr_report')

  const {data, isFetching, isError, term, isDebouncing, shouldFetch} = useGlobalSearch(
    query,
    'All',
    isOpen && availableServerCategories.length > 0
  )

  const trimmed = query.trim()
  const hasQuery = trimmed.length >= MIN_SEARCH_LENGTH

  // ----- Susun grup hasil -----
  const groups: DisplayGroup[] = useMemo(() => {
    if (!hasQuery) {
      const result: DisplayGroup[] = []
      if ((selectedCategory === 'All' || selectedCategory === 'Pages') && recent.length > 0) {
        result.push({category: 'Recent', items: recent})
      }

      if (selectedCategory === 'All') {
        result.push({category: 'Quick Links', items: pagesAsQuickLinks(accessiblePages, 20)})
      } else if (selectedCategory === 'Pages') {
        result.push({category: 'Pages', items: pagesAsQuickLinks(accessiblePages, 20)})
      } else {
        // Tab kategori spesifik (misal Clients, Invoices, Products, dsb) saat belum ada kata kunci
        const filteredPages = accessiblePages.filter((p) => {
          const matchCat = selectedCategory.toLowerCase()
          return (
            (p.category && p.category.toLowerCase() === matchCat) ||
            p.title.toLowerCase().includes(matchCat) ||
            p.id.toLowerCase().includes(matchCat) ||
            p.keywords.some((k) => k.toLowerCase().includes(matchCat))
          )
        })
        if (filteredPages.length > 0) {
          result.push({category: selectedCategory, items: pagesAsQuickLinks(filteredPages, 20)})
        }
      }
      return result
    }

    const result: DisplayGroup[] = []

    // Aksi: nomor telepon → CDR Report (tanpa query ke tabel as_cdr yang berisi jutaan baris)
    const digits = trimmed.replace(/[\s\-()]/g, '')
    if (canSearchCdr && /^\+?\d{6,}$/.test(digits) && (selectedCategory === 'All' || selectedCategory === 'Pages')) {
      result.push({
        category: 'Actions',
        items: [
          {
            key: `action-cdr-${digits}`,
            category: 'Actions',
            title: `Cari nomor "${digits}" di CDR Report`,
            subtitle: 'Buka CDR Report dengan filter nomor ini',
            url: `/cdr?search=${encodeURIComponent(digits)}`,
            icon: '📞',
          },
        ],
      })
    }

    // Hasil server (hanya jika term debounce sudah sama dengan input agar tidak "lompat")
    if (shouldFetch && data && data.query.toLowerCase() === term.toLowerCase()) {
      data.groups
        .filter((g) => selectedCategory === 'All' || g.category.toLowerCase() === selectedCategory.toLowerCase())
        .forEach((g) => {
          result.push({
            category: g.category,
            total: g.total,
            items: g.items.map((it) => ({
              key: `${g.category}-${it.id}`,
              category: g.category,
              title: it.title,
              subtitle: it.subtitle,
              tag: it.tag,
              url: it.url,
              icon: CATEGORY_ICONS[g.category] || '🔎',
            })),
          })
        })
    }

    // Halaman statis
    if (selectedCategory === 'All' || selectedCategory === 'Pages' || availableExtraCategories.includes(selectedCategory)) {
      const pagesLimit = selectedCategory === 'All' ? 4 : 10
      let candidatePages = accessiblePages
      if (selectedCategory !== 'All' && selectedCategory !== 'Pages') {
        const matchCat = selectedCategory.toLowerCase()
        candidatePages = accessiblePages.filter((p) => p.category && p.category.toLowerCase() === matchCat)
      }
      const pages = searchPages(candidatePages, trimmed, pagesLimit)
      if (pages.length > 0) result.push({category: selectedCategory === 'All' ? 'Pages' : selectedCategory, items: pages})
    }

    return result
  }, [hasQuery, recent, accessiblePages, trimmed, canSearchCdr, selectedCategory, shouldFetch, data, term])

  const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups])
  const isLoading = hasQuery && shouldFetch && (isDebouncing || isFetching)

  useEffect(() => {
    setActiveIndex(0)
  }, [trimmed, selectedCategory])

  // Pastikan item aktif selalu terlihat saat navigasi keyboard
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-search-index="${activeIndex}"]`)
    el?.scrollIntoView({block: 'nearest'})
  }, [activeIndex])

  const close = useCallback(() => {
    setIsOpen(false)
    inputRef.current?.blur()
  }, [])

  const handleSelectItem = useCallback(
    (item: DisplayItem) => {
      const entry: DisplayItem = {...item, key: `recent-${item.url}`}
      const next = [entry, ...recent.filter((r) => r.url !== item.url)].slice(0, MAX_RECENT)
      setRecent(next)
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next))
      } catch {
        /* storage penuh / private mode — abaikan */
      }
      setIsOpen(false)
      setQuery('')
      navigate(item.url)
    },
    [navigate, recent]
  )

  const clearRecent = () => {
    setRecent([])
    localStorage.removeItem(RECENT_KEY)
  }

  // Shortcut global Ctrl+K / Cmd+K dan klik di luar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsOpen(true)
        setTimeout(() => inputRef.current?.focus(), 50)
      }
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Navigasi keyboard di dalam input
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (flatItems.length ? (i + 1) % flatItems.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (flatItems.length ? (i - 1 + flatItems.length) % flatItems.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = flatItems[activeIndex]
      if (item) handleSelectItem(item)
    } else if (e.key === 'Tab' && isOpen) {
      e.preventDefault()
      const idx = categories.indexOf(selectedCategory)
      const nextIdx = e.shiftKey ? (idx - 1 + categories.length) % categories.length : (idx + 1) % categories.length
      setSelectedCategory(categories[nextIdx])
    } else if (e.key === 'Escape') {
      close()
    }
  }

  let runningIndex = -1

  return (
    <div ref={containerRef} className='ccs-general-search-wrapper position-relative'>
      {/* Search Input Bar */}
      <div
        className={clsx('ccs-search-input-pill d-flex align-items-center', isOpen && 'is-focused')}
        onClick={() => {
          setIsOpen(true)
          inputRef.current?.focus()
        }}
      >
        <span className='ccs-search-icon-wrapper d-flex align-items-center justify-content-center'>
          <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='11' cy='11' r='8'></circle>
            <line x1='21' y1='21' x2='16.65' y2='16.65'></line>
          </svg>
        </span>

        <input
          ref={inputRef}
          type='text'
          className='ccs-search-native-input flex-grow-1 border-0 bg-transparent'
          placeholder={isClientUser ? 'Cari invoice atau halaman...' : 'Quick search anything (Clients, Rates, Invoices...)'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleInputKeyDown}
          aria-label='Global search'
          autoComplete='off'
        />

        {isLoading ? (
          <span className='spinner-border spinner-border-sm text-muted me-2 spinner-14px' />
        ) : query ? (
          <button
            type='button'
            className='ccs-search-clear-btn btn p-0 border-0 bg-transparent text-muted me-1'
            onClick={(e) => {
              e.stopPropagation()
              setQuery('')
              inputRef.current?.focus()
            }}
            title='Clear'
          >
            ✕
          </button>
        ) : (
          <span className='ccs-search-kbd d-none d-sm-inline-flex align-items-center' title='Shortcut'>
            <span className='ccs-search-kbd-symbol'>Ctrl</span>
            <span className='ccs-search-kbd-key'>K</span>
          </span>
        )}
      </div>

      {/* Dropdown Menu Results */}
      {isOpen && (
        <div className='ccs-search-dropdown-menu position-absolute top-100 end-0 mt-2 shadow-lg'>
          {/* Category Tabs */}
          {categories.length > 2 && (
            <div className='ccs-search-tabs-row d-flex align-items-center gap-1 p-2 border-bottom'>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type='button'
                  className={clsx(
                    'ccs-search-tab-btn btn btn-sm py-1 px-2.5 rounded-pill fs-8 fw-bold border-0',
                    selectedCategory === cat && 'active'
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Results List */}
          <div ref={listRef} className='ccs-search-results-list py-1 search-results-scroll'>
            {hasQuery && isLoading && flatItems.length === 0 && (
              <>
                <div className='ccs-search-skeleton' />
                <div className='ccs-search-skeleton' />
                <div className='ccs-search-skeleton' />
              </>
            )}

            {hasQuery && isError && (
              <div className='text-center py-4 px-3 text-danger fs-7'>
                Gagal memuat hasil pencarian. Coba lagi beberapa saat.
              </div>
            )}

            {hasQuery && !isLoading && !isError && flatItems.length === 0 && (
              <div className='text-center py-5 px-3 text-muted fs-7'>
                <div className='mb-2 fs-4'>🔍</div>
                <div className='fw-bold'>Tidak ada hasil untuk &quot;{trimmed}&quot;</div>
                <div className='fs-8 mt-1'>
                  {isClientUser
                    ? 'Coba cari nomor invoice, periode (2026-10), atau nama halaman'
                    : 'Coba cari nama client, kode, NPWP, nomor invoice, produk, atau provider'}
                </div>
              </div>
            )}

            {!hasQuery && trimmed.length > 0 && (
              <div className='text-center py-3 px-3 text-muted fs-8'>Ketik minimal {MIN_SEARCH_LENGTH} karakter...</div>
            )}

            {groups.map((group) => (
              <div key={group.category}>
                <div className='ccs-search-group-title d-flex align-items-center justify-content-between'>
                  <span>
                    {group.category}
                    {group.total !== undefined && group.total > group.items.length && (
                      <span className='ms-1 text-muted text-lowercase fw-semibold'>
                        ({group.items.length} dari {group.total})
                      </span>
                    )}
                  </span>
                  {group.category === 'Recent' && (
                    <button
                      type='button'
                      className='btn btn-link p-0 fs-9 text-muted text-decoration-none'
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={clearRecent}
                    >
                      Hapus
                    </button>
                  )}
                </div>
                {group.items.map((item) => {
                  runningIndex += 1
                  const idx = runningIndex
                  return (
                    <div
                      key={item.key}
                      data-search-index={idx}
                      className={clsx(
                        'ccs-search-result-item d-flex align-items-center justify-content-between px-3 py-2 cursor-pointer',
                        idx === activeIndex && 'is-active'
                      )}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectItem(item)}
                    >
                      <div className='d-flex align-items-center gap-2.5 min-w-0'>
                        <span className='ccs-result-icon-badge flex-shrink-0 me-2'>
                          {item.icon || CATEGORY_ICONS[item.category] || '🔎'}
                        </span>
                        <div className='d-flex flex-column min-w-0'>
                          <span className='ccs-result-title fw-bold text-truncate'>
                            <Highlight text={item.title} term={trimmed} />
                          </span>
                          {item.subtitle && (
                            <span className='ccs-result-sub fs-8 text-muted text-truncate'>
                              <Highlight text={item.subtitle} term={trimmed} />
                            </span>
                          )}
                        </div>
                      </div>

                      <div className='d-flex align-items-center gap-2 flex-shrink-0 ms-2'>
                        {item.tag && (
                          <span className='badge ccs-result-tag fs-9 px-2 py-0.5 rounded-pill'>{item.tag}</span>
                        )}
                        <span className='ccs-result-arrow fs-7 text-muted'>→</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>

          {/* Footer Info */}
          <div className='ccs-search-dropdown-footer d-flex align-items-center justify-content-between px-3 py-2 border-top fs-8 text-muted'>
            <span>↑↓ navigasi · Enter buka · Tab kategori</span>
            <span>
              {hasQuery && data && shouldFetch && !isLoading ? `${data.tookMs} ms · ` : ''}ESC tutup
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
